import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChatText, Star, ShieldCheck, Clock, CarProfile, Package } from "@phosphor-icons/react";
import { BookingWidget } from "./BookingWidget";
import { PHONE, PHONE_RAW, RATING } from "../data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1500298281307-dcc46bf727d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const lines = ["ONE CALL.", "ONE TEXT.", "WE DO THE REST."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.15]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="DS Cabs taxi on the road" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-[100svh] grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center pt-28 pb-16">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center gap-3 mb-5"
          >
            <span className="inline-flex items-center gap-2 bg-sun text-ink text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5">
              <Clock weight="fill" size={14} /> 24/7 Service
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/25 text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5">
              Windsor, NS &amp; area
            </span>
          </motion.div>

          {/* Rating pill */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            data-testid="hero-rating"
            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 pl-3 pr-4 py-2 mb-6"
          >
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} weight="fill" size={16} className="text-sun" />
              ))}
            </div>
            <span className="text-white text-sm font-semibold">{RATING.score} on Google</span>
            <span className="hidden sm:inline text-white/60 text-xs">· {RATING.sub}</span>
          </motion.div>

          <h1 className="font-display font-extrabold text-white uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="reveal-line">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {i === 2 ? (<span>WE DO THE <span className="text-sun">REST.</span></span>) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="mt-6 max-w-xl text-white/85 text-base sm:text-lg leading-relaxed"
          >
            Windsor's most trusted premium taxi &amp; delivery. Airport runs, appointments, food,
            drinks, parcels &amp; gifts — booked in seconds and always on time.
          </motion.p>

          {/* Premium taxi + delivery pair — always side by side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }}
            data-testid="hero-modes"
            className="mt-7 grid grid-cols-2 gap-3 max-w-lg"
          >
            <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-3 min-w-0">
              <span className="w-10 h-10 bg-sun text-ink flex items-center justify-center shrink-0">
                <CarProfile weight="fill" size={22} />
              </span>
              <div className="leading-none min-w-0">
                <p className="text-white font-bold text-sm tracking-tight">Taxi</p>
                <p className="text-white/60 text-[10px] mt-1.5 uppercase tracking-[0.1em] leading-tight">Rides &amp; airport</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-3 min-w-0">
              <span className="w-10 h-10 bg-sun text-ink flex items-center justify-center shrink-0">
                <Package weight="fill" size={22} />
              </span>
              <div className="leading-none min-w-0">
                <p className="text-white font-bold text-sm tracking-tight">Delivery</p>
                <p className="text-white/60 text-[10px] mt-1.5 uppercase tracking-[0.1em] leading-tight">Food · Drinks · Parcels · Gifts</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
            className="mt-7 flex flex-wrap gap-3"
          >
            <a data-testid="hero-call-btn" href={`tel:${PHONE_RAW}`}
              className="flex items-center gap-2.5 bg-sun text-ink font-bold text-base px-6 py-3.5 border-2 border-sun shadow-hard hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all duration-150">
              <Phone weight="fill" size={20} /> Call {PHONE}
            </a>
            <a data-testid="hero-text-btn" href={`sms:${PHONE_RAW}`}
              className="flex items-center gap-2.5 bg-transparent text-white font-bold text-base px-6 py-3.5 border-2 border-white hover:bg-white hover:text-ink transition-colors duration-200">
              <ChatText weight="fill" size={20} /> Text us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15 }}
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-white/70 text-xs font-semibold uppercase tracking-wider"
          >
            <span className="flex items-center gap-1.5"><ShieldCheck weight="fill" size={15} className="text-sun" /> Professional drivers</span>
            <span className="flex items-center gap-1.5"><ShieldCheck weight="fill" size={15} className="text-sun" /> Clean &amp; comfortable</span>
            <span className="flex items-center gap-1.5"><ShieldCheck weight="fill" size={15} className="text-sun" /> Cash · Card · Tap · E-Transfer</span>
          </motion.div>
        </div>

        {/* Right: instant booking */}
        <div className="w-full max-w-md justify-self-start lg:justify-self-end">
          <BookingWidget />
        </div>
      </div>
    </section>
  );
};
